/**
 * User-flow step 2 — notification preferences for invited members.
 * Smart defaults: email-on-hearings + email-on-invoices + in-app pings.
 */
import { useState } from "react";
import { toast } from "sonner";
import { Bell } from "lucide-react";
import { api, apiError } from "@/lib/api";

const ITEMS = [
    { key: "email_hearings", label: "Email me about upcoming hearings",      hint: "Day-before reminder for cause-list matters" },
    { key: "email_invoices", label: "Email me about invoices",                hint: "When an invoice is created or paid" },
    { key: "email_chat",     label: "Email me about chat messages",           hint: "Only if I'm offline for >5 minutes" },
    { key: "sms_hearings",   label: "SMS me before hearings",                 hint: "Same-day morning reminder" },
    { key: "in_app",         label: "Show in-app notification banners",       hint: "Recommended — keeps you on top of activity" },
];

export default function PrefsStep({ state, onAdvance }) {
    const initial = state.details?.preferences || {};
    const [prefs, setPrefs] = useState({
        email_hearings: initial.email_hearings ?? true,
        email_invoices: initial.email_invoices ?? true,
        email_chat:     initial.email_chat     ?? false,
        sms_hearings:   initial.sms_hearings   ?? false,
        in_app:         initial.in_app         ?? true,
    });
    const [saving, setSaving] = useState(false);

    const toggle = (k) => setPrefs(p => ({ ...p, [k]: !p[k] }));

    const submit = async () => {
        setSaving(true);
        try {
            await api.patch("/onboarding/preferences", prefs);
            toast.success("Preferences saved");
            onAdvance?.();
        } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
    };

    return (
        <div className="max-w-md mx-auto space-y-5">
            <div className="text-center">
                <Bell className="w-12 h-12 text-primary mx-auto" strokeWidth={1.25}/>
                <h2 className="text-2xl font-semibold tracking-tight text-foreground mt-3">How should we ping you?</h2>
                <p className="text-muted-foreground text-sm mt-2">Change any of these later from your Profile.</p>
            </div>

            <div className="space-y-2">
                {ITEMS.map(it => (
                    <button key={it.key} type="button" onClick={() => toggle(it.key)}
                        data-testid={`onboarding-pref-${it.key}`}
                        className="w-full text-left flex items-center justify-between gap-3 px-4 py-3 border border-border hover:border-primary/40 bg-background">
                        <div>
                            <div className="text-sm text-foreground">{it.label}</div>
                            <div className="text-[10px] text-muted-foreground mt-0.5">{it.hint}</div>
                        </div>
                        <div className={`w-10 h-5 ${prefs[it.key] ? "bg-primary" : "bg-border"} relative transition shrink-0`}>
                            <span className={`block w-4 h-4 bg-white absolute top-0.5 transition ${prefs[it.key] ? "left-5" : "left-0.5"}`}/>
                        </div>
                    </button>
                ))}
            </div>

            <button onClick={submit} disabled={saving} data-testid="onboarding-prefs-save"
                className="w-full bg-primary text-primary-foreground hover:bg-primary disabled:opacity-30 px-6 py-2.5 text-xs uppercase tracking-[0.16em] font-medium">
                {saving ? "Saving…" : "Continue"}
            </button>
        </div>
    );
}
